import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Download } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  start_time: string;
  end_time: string;
  event_type: string;
}

export function CalendarSync() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [syncing, setSyncing] = useState(false);

  const syncToGoogleCalendar = async (event: CalendarEvent) => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-google-calendar', {
        body: { event },
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Event synced to Google Calendar',
      });
    } catch (error) {
      console.error('Error syncing to Google Calendar:', error);
      toast({
        title: 'Error',
        description: 'Failed to sync to Google Calendar',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const syncToOutlookCalendar = async (event: CalendarEvent) => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-outlook-calendar', {
        body: { event },
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Event synced to Outlook Calendar',
      });
    } catch (error) {
      console.error('Error syncing to Outlook Calendar:', error);
      toast({
        title: 'Error',
        description: 'Failed to sync to Outlook Calendar',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const downloadICalFile = async (eventId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('generate-ical', {
        body: { eventId },
      });

      if (error) throw error;

      // Create and download the iCal file
      const blob = new Blob([data.ical], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `event-${eventId}.ics`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Success',
        description: 'iCal file downloaded',
      });
    } catch (error) {
      console.error('Error generating iCal:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate iCal file',
        variant: 'destructive',
      });
    }
  };

  const handleGoogleConnect = () => {
    toast({
      title: 'Coming Soon',
      description: 'Google Calendar OAuth integration will be available soon. For now, use the iCal download feature.',
    });
  };

  const handleOutlookConnect = () => {
    toast({
      title: 'Coming Soon',
      description: 'Outlook Calendar OAuth integration will be available soon. For now, use the iCal download feature.',
    });
  };

  const handleICalDownload = async () => {
    try {
      const { data: events, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('user_id', user?.id)
        .gte('start_time', new Date().toISOString())
        .order('start_time', { ascending: true });

      if (error) throw error;

      if (!events || events.length === 0) {
        toast({
          title: 'No Events',
          description: 'You have no upcoming calendar events to export.',
        });
        return;
      }

      // Generate iCal content
      let icalContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Edugogy//Calendar//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH'
      ];

      events.forEach(event => {
        const startDate = new Date(event.start_time);
        const endDate = new Date(event.end_time);
        
        icalContent.push(
          'BEGIN:VEVENT',
          `UID:${event.id}@edugogy.com`,
          `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
          `DTSTART:${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
          `DTEND:${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
          `SUMMARY:${event.title}`,
          `DESCRIPTION:${event.description || ''}`,
          'END:VEVENT'
        );
      });

      icalContent.push('END:VCALENDAR');

      // Create and download the iCal file
      const blob = new Blob([icalContent.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `edugogy-calendar-${new Date().toISOString().split('T')[0]}.ics`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Success',
        description: `Downloaded ${events.length} event${events.length !== 1 ? 's' : ''} to calendar file.`,
      });
    } catch (error) {
      console.error('Error generating iCal:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate calendar file.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Calendar Integration
        </CardTitle>
        <CardDescription>
          Sync your meetings and course deadlines to your calendar
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <h4 className="font-medium">Available Integrations</h4>
          <div className="grid gap-2">
            <Button 
              variant="outline" 
              disabled={syncing} 
              className="justify-start"
              onClick={handleGoogleConnect}
            >
              <img
                src="https://www.google.com/favicon.ico"
                alt="Google"
                className="h-4 w-4 mr-2"
              />
              Connect Google Calendar
            </Button>
            <Button 
              variant="outline" 
              disabled={syncing} 
              className="justify-start"
              onClick={handleOutlookConnect}
            >
              <img
                src="https://www.microsoft.com/favicon.ico"
                alt="Microsoft"
                className="h-4 w-4 mr-2"
              />
              Connect Outlook Calendar
            </Button>
            <Button 
              variant="outline" 
              className="justify-start"
              onClick={handleICalDownload}
            >
              <Download className="h-4 w-4 mr-2" />
              Download as iCal File
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
